import { getSecureRandomBytes } from './random';

/**
 * W3C `traceparent` = `version-traceId-spanId-flags`, version `00`:
 * `00-<32 hex trace-id>-<16 hex span-id>-<2 hex flags>`.
 * @see https://www.w3.org/TR/trace-context/#traceparent-header
 */
export const TRACEPARENT_REGEX = /^00-[0-9a-f]{32}-[0-9a-f]{16}-[0-9a-f]{2}$/;

/**
 * trace-flags `01` = sampled. odin originates traces it wants recorded
 * end-to-end through the myosh backends, so every emitted root is sampled.
 */
const SAMPLED_FLAGS = '01';

const TRACE_ID_BYTES = 16;
const SPAN_ID_BYTES = 8;

const toHex = (bytes: Uint8Array): string => {
  let hex = '';
  for (const byte of bytes) {
    hex += byte.toString(16).padStart(2, '0');
  }
  return hex;
};

const isAllZero = (hex: string): boolean => /^0+$/.test(hex);

/**
 * `byteLength` secure random bytes as a lowercase hex string, or `null` when no
 * CSPRNG is available or the result is all-zero (invalid per W3C for ids).
 */
const secureHex = (byteLength: number): string | null => {
  const bytes = getSecureRandomBytes(byteLength);
  // Treat missing OR wrong-length output as "no entropy" so a misbehaving
  // custom RandomBytesSource can never yield a malformed id — omit instead.
  if (!bytes || bytes.length !== byteLength) {
    return null;
  }
  const hex = toHex(bytes);
  // W3C: an all-zero trace-id or parent-id is invalid and must not be sent.
  return isAllZero(hex) ? null : hex;
};

/**
 * Generate a fresh, contract-valid W3C `traceparent` root, or `null` when no
 * CSPRNG is available (the caller then omits the header — never a random
 * fallback).
 */
export const generateTraceparent = (): string | null => {
  const traceId = secureHex(TRACE_ID_BYTES);
  const spanId = secureHex(SPAN_ID_BYTES);
  if (!traceId || !spanId) {
    return null;
  }
  return `00-${traceId}-${spanId}-${SAMPLED_FLAGS}`;
};

/** True when `value` matches the W3C `traceparent` contract (with non-zero ids). */
export const isValidTraceparent = (value: string): boolean => {
  if (!TRACEPARENT_REGEX.test(value)) {
    return false;
  }
  const [, traceId, spanId] = value.split('-');
  return !isAllZero(traceId) && !isAllZero(spanId);
};
